import { useState, useMemo } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK_SHORT } from "@/lib/app";
import { ArrowDown, ArrowUp, CheckCircle, Lightning } from "@phosphor-icons/react";
import PinButton from "@/components/PinButton";
import PeriodOverrideBar from "@/components/PeriodOverrideBar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
} from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);

const VERDICT_LABELS = {
  uber: { label: "Ubrať", color: "var(--warning)", bg: "var(--warning-bg)", Icon: ArrowDown },
  pridaj: { label: "Pridať", color: "var(--critical)", bg: "var(--critical-bg)", Icon: ArrowUp },
  ok: { label: "OK", color: "var(--success)", bg: "var(--success-bg)", Icon: CheckCircle },
};

export default function PlanForward({ storeId, period }) {
  // Default period for "Plán personálu" = previous month + current month
  const defaultPlanPeriod = useMemo(() => {
    const today = new Date();
    const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: toISO(prevMonthStart), to: toISO(currentMonthEnd) };
  }, []);
  const [localPeriod, setLocalPeriod] = useState(defaultPlanPeriod);
  const qs = localPeriod?.from && localPeriod?.to ? `?period_from=${localPeriod.from}&period_to=${localPeriod.to}` : "";
  const { data, error } = useSWR(`/stores/${storeId}/analytics/plan-forward${qs}`, fetcher);
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false); // false = future only

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    let rows = data.rows;
    // Use effectiveShowAll if set in render scope, fall back to showAll
    const hasAnyFuture = rows.some((r) => r.is_future);
    const filterFuture = hasAnyFuture && !showAll;
    if (filterFuture) rows = rows.filter((r) => r.is_future);
    if (filter === "uber") rows = rows.filter((r) => r.verdict === "uber");
    else if (filter === "pridaj") rows = rows.filter((r) => r.verdict === "pridaj");
    else if (filter === "all_nonok") rows = rows.filter((r) => r.verdict !== "ok");
    return rows;
  }, [data, filter, showAll]);

  const chartByDay = useMemo(() => {
    if (!data?.rows) return [];
    const futureRows = data.rows.filter((r) => r.is_future);
    // If no future rows, fall back to ALL rows so the chart isn't empty
    const rows = futureRows.length > 0 ? futureRows : data.rows;
    const acc = {};
    for (const r of rows) {
      if (!acc[r.date]) {
        const [, mm, dd] = r.date.split("-");
        acc[r.date] = {
          date: r.date,
          short: `${dd}.${mm}. ${WEEKDAYS_SK_SHORT[r.weekday]}`,
          planned: 0,
          recommended: 0,
          predicted_revenue: 0,
          savings: 0,
          lift: 0,
        };
      }
      acc[r.date].planned += r.planned_employees;
      acc[r.date].recommended += r.recommended_employees;
      acc[r.date].predicted_revenue += r.predicted_revenue;
      acc[r.date].savings += r.savings_eur;
      acc[r.date].lift += r.revenue_lift_eur;
    }
    return Object.values(acc).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  if (error) {
    return (
      <div data-testid="plan-forward-section" className="space-y-3">
        <PeriodOverrideBar globalPeriod={defaultPlanPeriod} onChange={setLocalPeriod} label="Obdobie pre Plán dopredu" testIdPrefix="plan-forward-period" />
        <div className="card-flat p-6 text-[var(--critical)]">Chyba pri načítaní predikcie.</div>
      </div>
    );
  }
  if (!data) return (
    <div data-testid="plan-forward-section" className="space-y-3">
      <PeriodOverrideBar globalPeriod={defaultPlanPeriod} onChange={setLocalPeriod} label="Obdobie pre Plán dopredu" testIdPrefix="plan-forward-period" />
      <div className="text-[var(--text-secondary)]">Načítavam predikciu…</div>
    </div>
  );

  if (data.empty) {
    const reason =
      data.reason === "no_plan"
        ? `Pre obdobie ${localPeriod?.from} → ${localPeriod?.to} nie je nahraný hodinový plán. Skúste zmeniť obdobie alebo nahrať plán cez „Import dát".`
        : data.reason === "no_history"
        ? "Neexistuje žiadna história reálnych tržieb — nemám z čoho predikovať."
        : "Nedostatok dát.";
    return (
      <div data-testid="plan-forward-section" className="space-y-3">
        <PeriodOverrideBar globalPeriod={defaultPlanPeriod} onChange={setLocalPeriod} label="Obdobie pre Plán dopredu" testIdPrefix="plan-forward-period" />
        <div className="card-flat p-6">
          <h3 className="font-display text-base font-semibold mb-1 inline-flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-[var(--brand)]" /> Plán dopredu — návrh úprav
          </h3>
          <p className="text-sm text-[var(--text-secondary)]">{reason}</p>
        </div>
      </div>
    );
  }

  if (!data.summary.future_slots && !data.summary.past_slots) {
    return (
      <div data-testid="plan-forward-section" className="space-y-3">
        <PeriodOverrideBar globalPeriod={defaultPlanPeriod} onChange={setLocalPeriod} label="Obdobie pre Plán dopredu" testIdPrefix="plan-forward-period" />
        <div className="card-flat p-6">
          <h3 className="font-display text-base font-semibold mb-1 inline-flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-[var(--brand)]" /> Plán dopredu — návrh úprav
          </h3>
          <p className="text-sm text-[var(--text-secondary)]">
            Nemám čo zobraziť — plán ani realita neobsahujú žiadne sloty.
          </p>
        </div>
      </div>
    );
  }

  const s = data.summary;
  const hasFuture = (s.future_slots ?? 0) > 0;

  return (
    <div className="space-y-5" data-testid="plan-forward-section">
      <PeriodOverrideBar
        globalPeriod={defaultPlanPeriod}
        onChange={setLocalPeriod}
        label="Obdobie pre Plán dopredu"
        testIdPrefix="plan-forward-period"
      />

      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <h4 className="font-display text-sm font-semibold">
              {hasFuture ? "Plán vs odporúčanie po dňoch — budúce dni" : "Plán vs odporúčanie po dňoch — všetky plánované dni"}
            </h4>
            <PinButton
              storeId={storeId}
              kind="chart"
              label={`Graf Plán dopredu — návrh úprav (${data.future_period?.from} → ${data.future_period?.to})`}
              data={{ id: `pf_chart_${data.future_period?.from}_${data.future_period?.to}`, source: "plan_forward_chart" }}
              period={{ from: data.future_period?.from, to: data.future_period?.to }}
              testId="pin-chart-pf"
            />
          </div>
          <span className="text-xs text-[var(--text-muted)]">Posuvník dole = priblíženie výseku</span>
        </div>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={chartByDay} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
            <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="short" tick={{ fontSize: 10, fill: "#5E6962" }} angle={-30} textAnchor="end" interval={0} height={50} />
            <YAxis tick={{ fontSize: 10, fill: "#5E6962" }} />
            <Tooltip
              formatter={(v, n) => [fmtNum(v, 1), n]}
              contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="planned" name="Plán (os.hod)" fill="#C78220" isAnimationActive={false} radius={[2, 2, 0, 0]} />
            <Bar dataKey="recommended" name="Odporúčanie (os.hod)" fill="#2B4C38" isAnimationActive={false} radius={[2, 2, 0, 0]} />
            {chartByDay.length > 14 && (
              <Brush
                dataKey="short"
                height={26}
                stroke="#2B4C38"
                fill="#F1F3F2"
                travellerWidth={8}
                startIndex={0}
                endIndex={Math.min(chartByDay.length - 1, 13)}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h4 className="font-display text-sm font-semibold">Návrh úprav — sloty</h4>
            <p className="text-xs text-[var(--text-secondary)]">
              Predikovaná tržba = priemer histórie × (plánovaná denná tržba ÷ priemer dňa).
            </p>
          </div>
          <div className="flex gap-1 flex-wrap items-center">
            {[
              { id: "all", lbl: "Všetko" },
              { id: "all_nonok", lbl: "Len nezhody" },
              { id: "uber", lbl: "Ubrať" },
              { id: "pridaj", lbl: "Pridať" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`pf-filter-${f.id}`}
                className={`px-3 py-1.5 text-xs rounded-md ${filter === f.id ? "bg-[var(--brand)] text-white" : "btn-ghost"}`}
              >
                {f.lbl}
              </button>
            ))}
            {hasFuture && (
              <label className="inline-flex items-center gap-1.5 text-xs ml-2 cursor-pointer select-none" data-testid="toggle-show-all-plan-forward">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  className="accent-[var(--brand)]"
                />
                <span>Zobraziť aj minulé dni</span>
              </label>
            )}
          </div>
        </div>
        <div className="overflow-x-auto scroll-area max-h-[600px]">
          <table>
            <thead>
              <tr>
                <th>Dátum</th>
                <th>Hodina</th>
                <th className="text-right">Plán</th>
                <th className="text-right">Predik. tržba</th>
                <th className="text-right">Doporučené</th>
                <th className="text-right">Δ vs plán</th>
                <th>Verdikt</th>
                <th className="text-right">Úspora h / Tržba €</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let lastDate = null;
                return filteredRows.slice(0, 500).map((r, idx) => {
                  const v = VERDICT_LABELS[r.verdict] || VERDICT_LABELS.ok;
                  const isNewDate = r.date !== lastDate;
                  lastDate = r.date;
                  return (
                    <tr key={idx} className={isNewDate ? "border-t-2 border-[var(--border)]" : ""}>
                      <td className={isNewDate ? "font-semibold" : "text-[var(--text-muted)] text-xs"}>
                        {isNewDate ? `${r.date} (${WEEKDAYS_SK_SHORT[r.weekday]})${r.is_future ? "" : " ◷"}` : "↳"}
                      </td>
                      <td className="tabular">{r.hour}-{r.hour + 1}</td>
                      <td className="text-right tabular">{fmtNum(r.planned_employees, 1)}</td>
                      <td className="text-right tabular text-[var(--text-muted)]" title={`baseline ${fmtEur(r.baseline_hour_revenue)} × ${r.scale_factor}`}>{fmtEur(r.predicted_revenue)}</td>
                      <td className="text-right tabular font-medium text-[var(--brand)]">{fmtNum(r.recommended_employees, 1)}</td>
                      <td className={`text-right tabular ${r.delta_vs_plan > 0.5 ? "text-[var(--warning)]" : r.delta_vs_plan < -0.5 ? "text-[var(--critical)]" : "text-[var(--text-muted)]"}`}>
                        {r.delta_vs_plan > 0 ? "+" : ""}{fmtNum(r.delta_vs_plan, 1)}
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: v.bg, color: v.color }}>
                          <v.Icon size={12} weight="bold" /> {r.verdict_text}
                        </span>
                      </td>
                      <td className="text-right tabular text-xs">
                        {r.verdict === "uber" && r.delta_vs_plan > 0 && (
                          <span className="text-[var(--success)]">úspora {fmtNum(r.delta_vs_plan, 1)} h</span>
                        )}
                        {r.verdict === "pridaj" && r.revenue_lift_eur > 0 && (
                          <span className="text-[var(--critical)]">+{fmtEur(r.revenue_lift_eur)} tržba</span>
                        )}
                        {r.verdict === "ok" && <span className="text-[var(--text-muted)]">—</span>}
                      </td>
                      <td className="text-right">
                        <PinButton
                          storeId={storeId}
                          kind="day"
                          label={`Plán dopredu ${r.date} ${r.hour}-${r.hour + 1}: ${r.verdict_text} (plán ${fmtNum(r.planned_employees,1)}, doporučené ${fmtNum(r.recommended_employees,1)}, predik. tržba ${fmtEur(r.predicted_revenue)})`}
                          data={{ date: r.date, hour: r.hour, source: "plan_forward", ...r }}
                          period={{ from: r.date, to: r.date }}
                          testId={`pin-pf-${r.date}-${r.hour}`}
                        />
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
          {filteredRows.length === 0 && <div className="text-center text-[var(--text-secondary)] py-6 text-sm">Žiadne sloty pre tento filter.</div>}
        </div>
      </div>
    </div>
  );
}
